/**
 * Overture Maps PMTiles layer management.
 *
 * Adds Overture vector tile sources and styled layers to the MapLibre globe.
 * Data is streamed directly from S3 via HTTP range requests — no tile server needed.
 *
 * Dual-path architecture:
 *   PMTiles = "show" path (visual rendering, instant, global scale)
 *   DuckDB  = "tell" path (counts, rankings, tables — fired in parallel by query-router)
 *
 * Source-layer names and fields (from PMTiles metadata):
 *   base/water:             @name, subtype, class, is_salt, is_intermittent
 *   base/land:              @name, subtype, class
 *   base/land_use:          @name, subtype, class
 *   base/infrastructure:    @name, subtype, class
 *   places/place:           @name, categories (JSON string), addresses, brand, confidence
 *   transportation/segment: @name, subtype, class, subclass
 *   buildings/building:     @name, class, subtype, height, num_floors, has_parts
 *   buildings/building_part: @name, class, subtype, height, num_floors, building_id
 */

import { OVERTURE_PMTILES_BASE } from '../config.js';

// Overture tile base — repointed from the retired -beta S3 bucket to the
// public overturemaps-extras-us-west-2 tiles bucket (audit F-1). Configured
// in src/config.js so the base + release are not magic strings here.
const PMTILES_BASE = OVERTURE_PMTILES_BASE;

const OVERTURE_SOURCES = {
  'overture-base': `pmtiles://${PMTILES_BASE}/base.pmtiles`,
  'overture-places': `pmtiles://${PMTILES_BASE}/places.pmtiles`,
  'overture-buildings': `pmtiles://${PMTILES_BASE}/buildings.pmtiles`,
  'overture-transportation': `pmtiles://${PMTILES_BASE}/transportation.pmtiles`,
};

/**
 * Map from our category names to Overture categories JSON substring matches.
 * Overture's `categories` field is a JSON-encoded object like:
 *   {"main":"restaurant","alternate":["fast_food","pizza"]}
 * We use string containment checks since MapLibre can't parse JSON in expressions.
 */
const CATEGORY_FILTERS = {
  museum: ['in', 'museum', ['get', 'categories']],
  hospital: ['any',
    ['in', 'hospital', ['get', 'categories']],
    ['in', 'medical_center', ['get', 'categories']],
  ],
  school: ['any',
    ['in', 'school', ['get', 'categories']],
    ['in', 'university', ['get', 'categories']],
    ['in', 'college', ['get', 'categories']],
  ],
  airport: ['in', 'airport', ['get', 'categories']],
  restaurant: ['in', 'restaurant', ['get', 'categories']],
  cafe: ['any',
    ['in', 'cafe', ['get', 'categories']],
    ['in', 'coffee', ['get', 'categories']],
  ],
  hotel: ['in', 'hotel', ['get', 'categories']],
  library: ['in', 'library', ['get', 'categories']],
  church: ['in', 'church', ['get', 'categories']],
  mosque: ['in', 'mosque', ['get', 'categories']],
  temple: ['in', 'temple', ['get', 'categories']],
  synagogue: ['in', 'synagogue', ['get', 'categories']],
  park: ['any',
    ['in', 'national_park', ['get', 'categories']],
    ['in', 'nature_reserve', ['get', 'categories']],
    ['in', 'park', ['get', 'categories']],
  ],
  zoo: ['in', 'zoo', ['get', 'categories']],
  embassy: ['in', 'embassy', ['get', 'categories']],
  train_station: ['in', 'train_station', ['get', 'categories']],
  bus_station: ['in', 'bus_station', ['get', 'categories']],
  stadium: ['any',
    ['in', 'stadium', ['get', 'categories']],
    ['in', 'sports', ['get', 'categories']],
  ],
  shopping_mall: ['any',
    ['in', 'shopping_mall', ['get', 'categories']],
    ['in', 'shopping_center', ['get', 'categories']],
  ],
};

/**
 * Manages Overture Maps vector tile layers on the globe.
 */
class OvertureLayers {
  constructor(globe) {
    this.globe = globe;
    this.map = globe.map;
    this._activeSources = new Set();
    this._activeLayers = new Set();
  }

  /**
   * Ensure a source is added to the map.
   */
  _ensureSource(sourceId) {
    if (this.map.getSource(sourceId)) return;
    const url = OVERTURE_SOURCES[sourceId];
    if (!url) {
      console.warn(`[Overture] Unknown source: ${sourceId}`);
      return;
    }
    this.map.addSource(sourceId, {
      type: 'vector',
      url,
    });
    this._activeSources.add(sourceId);
    console.log(`[Overture] Added source: ${sourceId}`);
  }

  /**
   * Add a layer, ensuring its source exists first.
   */
  _addLayer(layerDef) {
    const sourceId = layerDef.source;
    this._ensureSource(sourceId);

    if (this.map.getLayer(layerDef.id)) {
      this.map.removeLayer(layerDef.id);
    }
    this.map.addLayer(layerDef);
    this._activeLayers.add(layerDef.id);
  }

  /**
   * Remove a layer by ID.
   */
  _removeLayer(layerId) {
    if (this.map.getLayer(layerId)) {
      this.map.removeLayer(layerId);
      this._activeLayers.delete(layerId);
    }
  }

  // ── Water layers (rivers, lakes, oceans) ──────────────────────

  showWater() {
    this.globe._exec(() => {
      try {
        this._ensureSource('overture-base');

        // Water area fills (lakes, reservoirs, oceans)
        this._addLayer({
          id: 'overture-water-fill',
          type: 'fill',
          source: 'overture-base',
          'source-layer': 'water',
          filter: ['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]],
          paint: {
            'fill-color': [
              'match', ['get', 'subtype'],
              'ocean', '#1a3a5c',
              'lake', '#2563a8',
              'reservoir', '#1e6fa0',
              'pond', '#3b7dc4',
              'river', '#2e8bc0',
              '#2563a8',
            ],
            'fill-opacity': [
              'interpolate', ['linear'], ['zoom'],
              0, 0.4,
              4, 0.55,
              8, 0.65,
            ],
          },
        });

        // Water lines (rivers, streams, canals)
        this._addLayer({
          id: 'overture-water-line',
          type: 'line',
          source: 'overture-base',
          'source-layer': 'water',
          filter: ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]],
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': [
              'match', ['get', 'subtype'],
              'river', '#4fc3f7',
              'stream', '#81d4fa',
              'canal', '#80cbc4',
              '#4fc3f7',
            ],
            'line-width': [
              'interpolate', ['linear'], ['zoom'],
              2, ['match', ['get', 'subtype'], 'river', 1, 0.3],
              6, ['match', ['get', 'subtype'], 'river', 2, 'canal', 1.5, 0.8],
              10, ['match', ['get', 'subtype'], 'river', 3, 'canal', 2, 'stream', 1.5, 1],
              14, ['match', ['get', 'subtype'], 'river', 5, 'canal', 3, 'stream', 2, 1.5],
            ],
            'line-opacity': 0.8,
          },
        });

        // Water labels (field is @name in Overture tiles)
        this._addLayer({
          id: 'overture-water-labels',
          type: 'symbol',
          source: 'overture-base',
          'source-layer': 'water',
          filter: ['has', '@name'],
          layout: {
            'symbol-placement': [
              'match', ['geometry-type'],
              'LineString', 'line',
              'point',
            ],
            'text-field': ['get', '@name'],
            'text-font': ['Noto Sans Regular'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 4, 10, 10, 13],
            'text-allow-overlap': false,
            'text-max-angle': 30,
          },
          paint: {
            'text-color': '#b3e5fc',
            'text-halo-color': 'rgba(0,0,0,0.85)',
            'text-halo-width': 1.5,
          },
          minzoom: 4,
        });

        console.log('[Overture] Water layers added');
      } catch (err) {
        console.error('[Overture] Failed to show water:', err);
      }
    });
  }

  hideWater() {
    this.globe._exec(() => {
      this._removeLayer('overture-water-fill');
      this._removeLayer('overture-water-line');
      this._removeLayer('overture-water-labels');
    });
  }

  // ── Land layers (land polygons, coastlines) ─────────────────

  showLand() {
    this.globe._exec(() => {
      try {
        this._ensureSource('overture-base');

        this._addLayer({
          id: 'overture-land-fill',
          type: 'fill',
          source: 'overture-base',
          'source-layer': 'land',
          paint: {
            'fill-color': [
              'match', ['coalesce', ['get', 'subtype'], ''],
              'glacier',  '#e8f4f8',
              'island',   '#3a5a40',
              'reef',     '#7fcdbb',
              'rock',     '#8b8680',
              'sand',     '#e8d5a3',
              '#2d4a2d', // default land
            ],
            'fill-opacity': [
              'interpolate', ['linear'], ['zoom'],
              0, 0.3,
              6, 0.45,
              10, 0.55,
            ],
          },
        });

        this._addLayer({
          id: 'overture-land-labels',
          type: 'symbol',
          source: 'overture-base',
          'source-layer': 'land',
          filter: ['has', '@name'],
          layout: {
            'text-field': ['get', '@name'],
            'text-font': ['Noto Sans Regular'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 4, 10, 10, 13],
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': '#c8d6c0',
            'text-halo-color': 'rgba(0,0,0,0.85)',
            'text-halo-width': 1.5,
          },
          minzoom: 6,
        });

        console.log('[Overture] Land layers added');
      } catch (err) {
        console.error('[Overture] Failed to show land:', err);
      }
    });
  }

  hideLand() {
    this.globe._exec(() => {
      this._removeLayer('overture-land-fill');
      this._removeLayer('overture-land-labels');
    });
  }

  // ── Land use layers (parks, forests, commercial zones) ──────

  showLandUse() {
    this.globe._exec(() => {
      try {
        this._ensureSource('overture-base');

        this._addLayer({
          id: 'overture-landuse-fill',
          type: 'fill',
          source: 'overture-base',
          'source-layer': 'land_use',
          paint: {
            'fill-color': [
              'match', ['coalesce', ['get', 'class'], ''],
              'residential',   '#a855f7',  // vivid purple
              'commercial',    '#f59e0b',  // vivid amber
              'industrial',    '#ef4444',  // vivid red
              'park',          '#22c55e',  // vivid green
              'forest',        '#15803d',  // deep green
              'recreation',    '#34d399',  // emerald
              'cemetery',      '#6b7280',  // gray
              'military',      '#78716c',  // stone
              'education',     '#3b82f6',  // vivid blue
              'medical',       '#f43f5e',  // rose
              'religious',     '#d97706',  // amber-dark
              'airport',       '#8b5cf6',  // violet
              'farmland',      '#a3e635',  // lime
              'aquaculture',   '#06b6d4',  // cyan
              'winter_sports', '#7dd3fc',  // sky
              '#9ca3af', // default gray
            ],
            'fill-opacity': 0.65,
            'fill-outline-color': 'rgba(255,255,255,0.3)',
          },
          minzoom: 4,
        });

        this._addLayer({
          id: 'overture-landuse-labels',
          type: 'symbol',
          source: 'overture-base',
          'source-layer': 'land_use',
          filter: ['has', '@name'],
          layout: {
            'text-field': ['get', '@name'],
            'text-font': ['Noto Sans Regular'],
            'text-size': 10,
            'text-allow-overlap': false,
            'text-padding': 4,
          },
          paint: {
            'text-color': '#d0d8c0',
            'text-halo-color': 'rgba(0,0,0,0.85)',
            'text-halo-width': 1.5,
          },
          minzoom: 10,
        });

        console.log('[Overture] Land use layers added');
      } catch (err) {
        console.error('[Overture] Failed to show land use:', err);
      }
    });
  }

  hideLandUse() {
    this.globe._exec(() => {
      this._removeLayer('overture-landuse-fill');
      this._removeLayer('overture-landuse-labels');
    });
  }

  // ── Transportation layers (roads, railways) ──────────────────

  showTransportation() {
    this.globe._exec(() => {
      try {
        this._ensureSource('overture-transportation');

        // Road network
        this._addLayer({
          id: 'overture-roads',
          type: 'line',
          source: 'overture-transportation',
          'source-layer': 'segment',
          filter: ['==', ['get', 'subtype'], 'road'],
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': [
              'match', ['get', 'class'],
              'motorway', '#ff8c00',
              'primary', '#ffaa33',
              'secondary', '#ffcc66',
              'tertiary', '#ffe099',
              'residential', 'rgba(255,255,255,0.3)',
              'rgba(255,255,255,0.2)',
            ],
            'line-width': [
              'interpolate', ['linear'], ['zoom'],
              4, ['match', ['get', 'class'], 'motorway', 1, 'primary', 0.5, 0],
              8, ['match', ['get', 'class'], 'motorway', 2, 'primary', 1.5, 'secondary', 1, 0.5],
              12, ['match', ['get', 'class'], 'motorway', 4, 'primary', 3, 'secondary', 2, 'tertiary', 1.5, 1],
              16, ['match', ['get', 'class'], 'motorway', 8, 'primary', 6, 'secondary', 4, 'tertiary', 3, 2],
            ],
            'line-opacity': 0.8,
          },
          minzoom: 4,
        });

        // Rail network
        this._addLayer({
          id: 'overture-rail',
          type: 'line',
          source: 'overture-transportation',
          'source-layer': 'segment',
          filter: ['==', ['get', 'subtype'], 'rail'],
          layout: {
            'line-cap': 'butt',
            'line-join': 'miter',
          },
          paint: {
            'line-color': '#e0e0e0',
            'line-width': [
              'interpolate', ['linear'], ['zoom'],
              6, 0.5,
              10, 1,
              14, 2,
            ],
            'line-dasharray': [4, 2],
            'line-opacity': 0.6,
          },
          minzoom: 6,
        });

        // Road labels (field is @name in Overture tiles)
        this._addLayer({
          id: 'overture-road-labels',
          type: 'symbol',
          source: 'overture-transportation',
          'source-layer': 'segment',
          filter: ['all',
            ['==', ['get', 'subtype'], 'road'],
            ['in', ['get', 'class'], ['literal', ['motorway', 'primary', 'secondary']]],
            ['has', '@name'],
          ],
          layout: {
            'symbol-placement': 'line',
            'text-field': ['get', '@name'],
            'text-font': ['Noto Sans Regular'],
            'text-size': 10,
            'text-allow-overlap': false,
            'text-max-angle': 30,
          },
          paint: {
            'text-color': '#ffd580',
            'text-halo-color': 'rgba(0,0,0,0.85)',
            'text-halo-width': 1.5,
          },
          minzoom: 10,
        });

        console.log('[Overture] Transportation layers added');
      } catch (err) {
        console.error('[Overture] Failed to show transportation:', err);
      }
    });
  }

  hideTransportation() {
    this.globe._exec(() => {
      this._removeLayer('overture-roads');
      this._removeLayer('overture-rail');
      this._removeLayer('overture-road-labels');
    });
  }

  // ── Building layers ──────────────────────────────────────────

  /**
   * Class-based building colors for visual distinction.
   * Overture building classes: residential, commercial, industrial, agricultural,
   * civic, education, entertainment, medical, military, outbuilding, religious,
   * service, transportation, etc.
   */
  static get BUILDING_COLORS() {
    return [
      'match', ['coalesce', ['get', 'class'], ''],
      'residential',     '#6fa8dc',  // soft blue
      'commercial',      '#e8a838',  // amber
      'industrial',      '#cc6677',  // muted red
      'agricultural',    '#8fbc8f',  // sage green
      'civic',           '#9b88c4',  // soft purple
      'education',       '#4a90d9',  // bright blue
      'entertainment',   '#e06c9f',  // pink
      'medical',         '#e05555',  // red
      'military',        '#8b8b6f',  // olive
      'outbuilding',     '#999999',  // gray
      'religious',       '#d4a857',  // gold
      'service',         '#7ec8a0',  // mint
      'transportation',  '#b0a0d0',  // lavender
      '#b4b4c8',  // default: neutral gray-blue
    ];
  }

  showBuildings() {
    this.globe._exec(() => {
      try {
        this._ensureSource('overture-buildings');

        // Disable terrain — it warps fill-extrusion, making buildings look lumpy
        this._terrainWasActive = !!this.map.getTerrain();
        if (this._terrainWasActive) {
          this.map.setTerrain(null);
        }

        // Height expression: prefer height field, then estimate from num_floors (3.5m/floor)
        const heightExpr = [
          'coalesce',
          ['get', 'height'],
          ['*', ['coalesce', ['get', 'num_floors'], 0], 3.5],
        ];
        // Use the computed height, but floor at 4m minimum so flat buildings are still visible
        const finalHeight = ['max', heightExpr, 4];

        // Flat fill at zoom 13-14 (before extrusion kicks in)
        this._addLayer({
          id: 'overture-buildings-fill',
          type: 'fill',
          source: 'overture-buildings',
          'source-layer': 'building',
          paint: {
            'fill-color': OvertureLayers.BUILDING_COLORS,
            'fill-opacity': 0.4,
            'fill-outline-color': 'rgba(200,200,220,0.6)',
          },
          minzoom: 13,
          maxzoom: 15, // fade out as 3D takes over
        });

        // 3D fill-extrusion layer
        this._addLayer({
          id: 'overture-buildings-3d',
          type: 'fill-extrusion',
          source: 'overture-buildings',
          'source-layer': 'building',
          paint: {
            'fill-extrusion-color': OvertureLayers.BUILDING_COLORS,
            'fill-extrusion-height': finalHeight,
            'fill-extrusion-base': 0,
            'fill-extrusion-opacity': [
              'interpolate', ['linear'], ['zoom'],
              14, 0.85,
              16, 0.92,
              18, 1.0,
            ],
          },
          minzoom: 14,
        });

        // Building parts — sub-structures (towers, wings) with their own height
        this._addLayer({
          id: 'overture-buildings-parts-3d',
          type: 'fill-extrusion',
          source: 'overture-buildings',
          'source-layer': 'building_part',
          paint: {
            'fill-extrusion-color': OvertureLayers.BUILDING_COLORS,
            'fill-extrusion-height': finalHeight,
            'fill-extrusion-base': ['coalesce', ['get', 'min_height'], 0],
            'fill-extrusion-opacity': [
              'interpolate', ['linear'], ['zoom'],
              14, 0.85,
              16, 0.92,
              18, 1.0,
            ],
          },
          minzoom: 15,
        });

        // Building labels at high zoom
        this._addLayer({
          id: 'overture-buildings-labels',
          type: 'symbol',
          source: 'overture-buildings',
          'source-layer': 'building',
          filter: ['has', '@name'],
          layout: {
            'text-field': ['get', '@name'],
            'text-font': ['Noto Sans Regular'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 16, 9, 19, 12],
            'text-anchor': 'center',
            'text-allow-overlap': false,
            'text-padding': 6,
            'text-max-width': 8,
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': 'rgba(0,0,0,0.85)',
            'text-halo-width': 1.5,
          },
          minzoom: 16,
        });

        // Auto-pitch camera for 3D viewing (only if not already flying)
        if (!this.globe._isFlying) {
          const currentPitch = this.map.getPitch();
          if (currentPitch < 40) {
            this.globe.stopAutoRotate();
            this.map.easeTo({ pitch: 60, duration: 1000 });
          }
        }

        console.log('[Overture] 3D building layers added (terrain disabled for clean extrusion)');
      } catch (err) {
        console.error('[Overture] Failed to show buildings:', err);
      }
    });
  }

  hideBuildings() {
    this.globe._exec(() => {
      this._removeLayer('overture-buildings-fill');
      this._removeLayer('overture-buildings-3d');
      this._removeLayer('overture-buildings-parts-3d');
      this._removeLayer('overture-buildings-labels');

      // Restore terrain if it was active before buildings were shown
      if (this._terrainWasActive) {
        this.map.setTerrain({ source: 'maptiler-terrain', exaggeration: 1.5 });
        this._terrainWasActive = false;
      }

      // Restore flat pitch
      this.globe.stopAutoRotate();
      this.map.easeTo({ pitch: 0, duration: 800 });
    });
  }

  // ── Places layers (POIs from vector tiles) ───────────────────

  /**
   * Show Overture places as circle markers.
   * @param {object} options
   * @param {string} options.category - Filter to a specific category (e.g., 'museum', 'hospital')
   */
  showPlaces(options = {}) {
    this.globe._exec(() => {
      try {
        this._ensureSource('overture-places');

        // Build category filter using the `categories` JSON string field
        let filter;
        if (options.category) {
          filter = CATEGORY_FILTERS[options.category]
            || ['in', options.category, ['get', 'categories']];
        }

        // Color by primary category substring matching
        const categoryColors = [
          'case',
          ['in', 'school', ['coalesce', ['get', 'categories'], '']], '#4285f4',
          ['in', 'university', ['coalesce', ['get', 'categories'], '']], '#4285f4',
          ['in', 'hospital', ['coalesce', ['get', 'categories'], '']], '#ea4335',
          ['in', 'airport', ['coalesce', ['get', 'categories'], '']], '#9c27b0',
          ['in', 'train_station', ['coalesce', ['get', 'categories'], '']], '#ff9800',
          ['in', 'museum', ['coalesce', ['get', 'categories'], '']], '#009688',
          ['in', 'library', ['coalesce', ['get', 'categories'], '']], '#009688',
          ['in', 'mosque', ['coalesce', ['get', 'categories'], '']], '#ffc107',
          ['in', 'church', ['coalesce', ['get', 'categories'], '']], '#ffc107',
          ['in', 'temple', ['coalesce', ['get', 'categories'], '']], '#ffc107',
          ['in', 'park', ['coalesce', ['get', 'categories'], '']], '#4caf50',
          ['in', 'restaurant', ['coalesce', ['get', 'categories'], '']], '#ff5722',
          ['in', 'cafe', ['coalesce', ['get', 'categories'], '']], '#795548',
          ['in', 'coffee', ['coalesce', ['get', 'categories'], '']], '#795548',
          ['in', 'hotel', ['coalesce', ['get', 'categories'], '']], '#3f51b5',
          ['in', 'shopping', ['coalesce', ['get', 'categories'], '']], '#ff6f00',
          '#9e9e9e', // default
        ];

        this._addLayer({
          id: 'overture-places-circle',
          type: 'circle',
          source: 'overture-places',
          'source-layer': 'place',
          ...(filter ? { filter } : {}),
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 1.5, 8, 3, 12, 5, 16, 8],
            'circle-color': categoryColors,
            'circle-opacity': 0.85,
            'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 4, 0, 8, 0.5, 12, 1],
            'circle-stroke-color': 'rgba(255,255,255,0.7)',
          },
          minzoom: 4,
        });

        // Place labels at higher zoom (field is @name)
        this._addLayer({
          id: 'overture-places-labels',
          type: 'symbol',
          source: 'overture-places',
          'source-layer': 'place',
          ...(filter ? { filter } : {}),
          layout: {
            'text-field': ['coalesce', ['get', '@name'], ''],
            'text-font': ['Noto Sans Regular'],
            'text-size': 11,
            'text-offset': [0, 1.2],
            'text-anchor': 'top',
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': 'rgba(0,0,0,0.8)',
            'text-halo-width': 1.5,
          },
          minzoom: 12,
        });

        console.log(`[Overture] Places layers added${options.category ? ` (category: ${options.category})` : ''}`);
      } catch (err) {
        console.error('[Overture] Failed to show places:', err);
      }
    });
  }

  hidePlaces() {
    this.globe._exec(() => {
      this._removeLayer('overture-places-circle');
      this._removeLayer('overture-places-labels');
    });
  }

  // ── Layer visibility toggle ──────────────────────────────────

  /**
   * Toggle a theme on/off.
   * @param {'water'|'transportation'|'buildings'|'places'} theme
   * @param {boolean} visible
   * @param {object} options - Extra options (e.g., category for places)
   */
  toggleTheme(theme, visible, options = {}) {
    const actions = {
      water: { show: () => this.showWater(), hide: () => this.hideWater() },
      land: { show: () => this.showLand(), hide: () => this.hideLand() },
      land_use: { show: () => this.showLandUse(), hide: () => this.hideLandUse() },
      transportation: { show: () => this.showTransportation(), hide: () => this.hideTransportation() },
      buildings: { show: () => this.showBuildings(), hide: () => this.hideBuildings() },
      places: { show: () => this.showPlaces(options), hide: () => this.hidePlaces() },
    };

    const action = actions[theme];
    if (!action) {
      console.warn(`[Overture] Unknown theme: ${theme}`);
      return;
    }

    if (visible) {
      action.show();
    } else {
      action.hide();
    }
  }

  /**
   * Check if a theme's layers are currently visible.
   */
  isThemeVisible(theme) {
    const layerIds = {
      water: ['overture-water-fill', 'overture-water-line'],
      land: ['overture-land-fill'],
      land_use: ['overture-landuse-fill'],
      transportation: ['overture-roads', 'overture-rail'],
      buildings: ['overture-buildings-fill'],
      places: ['overture-places-circle'],
    };

    const ids = layerIds[theme];
    if (!ids) return false;
    return ids.some(id => this._activeLayers.has(id));
  }

  /**
   * Remove all Overture layers and sources.
   */
  removeAll() {
    this.globe._exec(() => {
      for (const layerId of [...this._activeLayers]) {
        this._removeLayer(layerId);
      }
      for (const sourceId of [...this._activeSources]) {
        if (this.map.getSource(sourceId)) {
          this.map.removeSource(sourceId);
        }
        this._activeSources.delete(sourceId);
      }
    });
  }

  /**
   * Get list of active Overture layer IDs (for popup interactivity).
   */
  getInteractiveLayers() {
    const interactive = [];
    if (this._activeLayers.has('overture-places-circle')) interactive.push('overture-places-circle');
    if (this._activeLayers.has('overture-water-fill')) interactive.push('overture-water-fill');
    if (this._activeLayers.has('overture-water-line')) interactive.push('overture-water-line');
    if (this._activeLayers.has('overture-land-fill')) interactive.push('overture-land-fill');
    if (this._activeLayers.has('overture-landuse-fill')) interactive.push('overture-landuse-fill');
    if (this._activeLayers.has('overture-roads')) interactive.push('overture-roads');
    if (this._activeLayers.has('overture-buildings-fill')) interactive.push('overture-buildings-fill');
    if (this._activeLayers.has('overture-buildings-3d')) interactive.push('overture-buildings-3d');
    return interactive;
  }
}

export { OvertureLayers, OVERTURE_SOURCES, PMTILES_BASE, CATEGORY_FILTERS };
export default OvertureLayers;
